import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Divider, Typography } from "@mzc-pdc/ui";
import { IconPreview, IconCompleteCircle, IconErrorCircle, IconInfoCircle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { ASSET_PREVIEW_STATUS, ASSET_PREVIEW_TYPE, ASSET_PROXY_STATUS, MEDIA_TYPES } from "@constants";

/**
 * 프록시 영상 추출 상태.
 *
 * 프록시 = AI 분석·미리보기에 쓰는 저용량 사본(`previews[].type === LOW_QUALITY_VIDEO`).
 * 원본 인제스트와 별개의 트랜스코딩 잡으로 뒤늦게 만들어지는데 지금까지 화면에 드러나지 않아,
 * 사용자는 AI 분석이 프록시로 갈지 원본으로 갈지 알 수 없었다. (DAM-457)
 *
 * 썸네일·GIF 등 다른 프리뷰는 보지 않는다 - 그쪽은 썸네일 표시 로직의 관심사이고,
 * 여기서 섞으면 "무엇이 남았는지"가 흐려진다.
 */

/**
 * 프록시 1건의 원시 status 를 정규화한다.
 * @param {object} preview asset.previews[] 원소
 * @returns {"IN_PROGRESS"|"COMPLETE"|"ERROR"|"NOT_SUPPORTED"}
 */
export const getPreviewItemStatus = (preview) => {
    const normalized = String(preview?.status ?? "")
        .trim()
        .toUpperCase();

    if (normalized === ASSET_PREVIEW_STATUS.COMPLETE) return ASSET_PREVIEW_STATUS.COMPLETE;
    if (normalized === ASSET_PREVIEW_STATUS.ERROR) return ASSET_PREVIEW_STATUS.ERROR;
    if (normalized === ASSET_PREVIEW_STATUS.NOT_SUPPORTED) return ASSET_PREVIEW_STATUS.NOT_SUPPORTED;
    // 아는 값이 아니면 진행 중으로 본다. BE 가 새 중간 상태를 추가했을 때 "완료"로 폴백하면
    // 아직 만들어지는 중인 프록시를 준비됐다고 말하게 되는데, 그게 이 티켓이 없애려는 문제다.
    // 형제 컴포넌트(AssetSearchIndexStatus)도 같은 방향으로 방어한다.
    return ASSET_PREVIEW_STATUS.IN_PROGRESS;
};

/**
 * 프록시 상태를 노출하는 자산 유형인지. 프록시는 영상 계열에서만 만들어진다.
 * @param {object} asset
 * @returns {boolean}
 */
export const isAssetProxySupported = (asset) => asset?.mediaType === MEDIA_TYPES.VIDEO;

// 서버 프리뷰 status → 화면 표시 status. items 와 집계 상태가 같은 어휘를 쓰게 한다.
const TO_PROXY_STATUS = {
    [ASSET_PREVIEW_STATUS.COMPLETE]: ASSET_PROXY_STATUS.COMPLETED,
    [ASSET_PREVIEW_STATUS.IN_PROGRESS]: ASSET_PROXY_STATUS.IN_PROGRESS,
    [ASSET_PREVIEW_STATUS.ERROR]: ASSET_PROXY_STATUS.FAILED,
};

/**
 * 자산의 프록시 추출 상태를 구한다. 표시 전용 파생이며 데이터를 새로 가져오지 않는다.
 *
 * 프록시는 영상 자산당 **최대 1개**다(BE 확인, 2026-08-13). 프로젝트 설정도 on/off 하나뿐이고
 * 추출 잡도 하나다. 그래서 "몇 개 중 몇 개" 개념이 없다.
 *
 * `previews` 에서 프록시만 골라 보므로, 썸네일 프리뷰 판정(`previews[0]`)이
 * 화면마다 다른 문제와 무관하게 동작한다.
 *
 * @param {object} asset previews · mediaType 사용
 * @returns {null|{status: string, size: number|undefined}} 대상이 아니면 null
 */
export const getAssetProxyStatus = (asset) => {
    if (!asset) return null;

    // 영상이 아니면 프록시 개념이 없다.
    if (!isAssetProxySupported(asset)) return null;

    const proxy = (asset.previews ?? []).find((preview) => preview?.type === ASSET_PREVIEW_TYPE.LOW_QUALITY_VIDEO);
    if (!proxy) return null;

    const itemStatus = getPreviewItemStatus(proxy);
    // 미지원은 실패가 아니다. 만들 수 없는 자산이니 말할 상태가 없다.
    if (itemStatus === ASSET_PREVIEW_STATUS.NOT_SUPPORTED) return null;

    return { status: TO_PROXY_STATUS[itemStatus], size: proxy?.size };
};

/**
 * 훑어보는 맥락(목록 · 상세 헤더)에 올릴 상태인지. 완료는 기본 기대 상태라 감춘다.
 *
 * 목록 전 행을 완료 배지로 채우면 정작 봐야 할 "생성 중"이 묻힌다.
 * 완료를 포함한 전체 상태는 우측 패널 · 상세 개요 · 랜디션 표가 맡는다.
 */
export const isAssetProxyStatusNoteworthy = (proxy) => !!proxy && proxy.status !== ASSET_PROXY_STATUS.COMPLETED;

// 상태별 색. 형제 요소인 AssetSearchIndexStatus 와 같은 토큰 규칙(main / 50 배경 / 200 테두리).
const STATUS_CONFIG = {
    [ASSET_PROXY_STATUS.COMPLETED]: {
        color: theme.palette.success.main,
        backgroundColor: theme.palette.success[50],
        borderColor: theme.palette.success[200],
    },
    [ASSET_PROXY_STATUS.IN_PROGRESS]: {
        color: theme.palette.info.main,
        backgroundColor: theme.palette.info[50],
        borderColor: theme.palette.info[200],
    },
    [ASSET_PROXY_STATUS.FAILED]: {
        color: theme.palette.error.main,
        backgroundColor: theme.palette.error[50],
        borderColor: theme.palette.error[200],
    },
};

const SIZE_CONFIG = {
    sm: { iconSize: 12, sx: { width: 20, height: 20, justifyContent: "center" } },
    md: { iconSize: 12, sx: { height: 20, px: 0.5, gap: 0.25 } },
    lg: { iconSize: 16, sx: { height: 25, px: 1, gap: 0.75 } },
};

// 인덱싱 배지와 문구를 맞추기 위해 이미 등록된 상태 enum 키를 재사용한다.
// PENDING = "진행 중", COMPLETED = "완료", FAILED = "실패".
const STATUS_LABEL_KEY = {
    [ASSET_PROXY_STATUS.COMPLETED]: "COMPLETED",
    [ASSET_PROXY_STATUS.IN_PROGRESS]: "PENDING",
    [ASSET_PROXY_STATUS.FAILED]: "FAILED",
};

// 옆에 항목명이 없는 자리(상세 제목행)에서 쓰는 자립형 라벨.
// "진행 중"만 있으면 무엇이 진행 중인지 알 수 없어 도메인을 문구에 포함한다.
const STANDALONE_LABEL_KEY = {
    [ASSET_PROXY_STATUS.COMPLETED]: "Proxy video ready",
    [ASSET_PROXY_STATUS.IN_PROGRESS]: "Extracting proxy video",
    [ASSET_PROXY_STATUS.FAILED]: "Proxy extraction failed",
};

const INFO_MESSAGE_KEY = {
    [ASSET_PROXY_STATUS.COMPLETED]: "AI analysis uses the proxy video.",
    [ASSET_PROXY_STATUS.IN_PROGRESS]: "Creating a proxy video. Analyzing now uses the original and takes longer.",
    [ASSET_PROXY_STATUS.FAILED]: "The proxy video could not be created. AI analysis will use the original.",
};

// 프록시는 미디어 업계 통용어지만 콘솔에서 처음 쓰는 말이라 툴팁에서 한 번 풀어준다.
const GLOSS_KEY = "Proxy video - a small copy used for AI analysis and preview.";
// 설정이 꺼진 프로젝트는 소비처가 항목 자체를 숨기므로(DAM-444) 이 문구는 설정이 켜진
// 프로젝트에서만 뜬다. 주 사례는 "설정은 켰는데 그 이전에 만들어진 자산"이라 사유를 그대로 말한다.
// (업로드 직후 프리뷰 항목이 생기기 전 몇 초 동안도 이 문구가 뜰 수 있으나 곧 진행 중으로 바뀐다.)
const ABSENT_REASON_KEY =
    "No proxy video. Proxy auto-generation applies only to videos registered after it was selected.";

const IN_PROGRESS_STATUSES = [ASSET_PROXY_STATUS.IN_PROGRESS];

/**
 * 프록시 영상 추출 상태를 아이콘 pill 로 표시한다.
 *
 * 골격·토큰·툴팁 구조는 AssetSearchIndexStatus 를 따르고, 도메인 아이콘만
 * 인덱싱(IconIndexing)과 구분되는 IconPreview 를 쓴다.
 *
 * 색만으로 상태를 읽지 않도록 ①진행형은 스피너(움직임) ②툴팁 텍스트
 * ③aria-label 전체 문장을 함께 제공한다.
 *
 * @param {object} props
 * @param {string} props.status - ASSET_PROXY_STATUS 값
 * @param {"sm"|"md"|"lg"} [props.size="md"] - sm=썸네일 배지, md=목록·패널, lg=상세 헤더
 * @param {boolean} [props.showLabel=false] - pill 우측에 상태 텍스트 노출
 * @param {boolean} [props.standalone=false] - 옆에 항목명이 없는 자리(상세 제목행)에서
 *        "프록시 추출 중"처럼 도메인을 포함한 자립형 라벨을 쓴다
 * @param {object} [props.sx]
 */
const AssetProxyStatus = ({ status, size = "md", showLabel = false, standalone = false, sx }) => {
    const { t } = useTranslation();

    const statusConfig = STATUS_CONFIG[status] ?? STATUS_CONFIG[ASSET_PROXY_STATUS.IN_PROGRESS];
    const sizeConfig = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
    const { iconSize } = sizeConfig;
    const isSmall = size === "sm";
    const isInProgress = IN_PROGRESS_STATUSES.includes(status);
    const isFailed = status === ASSET_PROXY_STATUS.FAILED;

    const domainLabel = t("common::label::Proxy video", "Proxy video");
    const labelSet = standalone ? STANDALONE_LABEL_KEY : STATUS_LABEL_KEY;
    const statusLabelKey = labelSet[status] ?? labelSet[ASSET_PROXY_STATUS.IN_PROGRESS];
    const statusLabel = t(`common::label::${statusLabelKey}`, statusLabelKey);
    // 툴팁 chip 은 항상 짧은 상태어를 쓴다 - 바로 위에 "프록시 영상" 제목이 있어 중복이 된다.
    const chipLabelKey = STATUS_LABEL_KEY[status] ?? STATUS_LABEL_KEY[ASSET_PROXY_STATUS.IN_PROGRESS];
    const chipLabel = t(`common::label::${chipLabelKey}`, chipLabelKey);
    const infoMessageKey = INFO_MESSAGE_KEY[status];
    const infoMessage = infoMessageKey ? t(`common::msg::${infoMessageKey}`, infoMessageKey) : "";

    // 색·아이콘 없이도 상태를 읽을 수 있도록 전체 문장을 접근성 이름으로 제공한다.
    const ariaLabel = [domainLabel, statusLabel, infoMessage].filter(Boolean).join(" ");

    const renderStatusIcon = () => {
        if (isInProgress) return <CircularProgress size={iconSize} color={"inherit"} />;
        if (isFailed) return <IconErrorCircle size={iconSize} color={"inherit"} />;
        return <IconCompleteCircle size={iconSize} color={"inherit"} />;
    };

    // sm 은 아이콘만 남아 색이 유일한 단서가 되므로, 진행 중일 때 pill 둘레에 스피너 링을 얹는다.
    const renderDomainIcon = () =>
        isSmall && isInProgress ? (
            <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <IconPreview size={iconSize} color={"inherit"} />
                <CircularProgress
                    size={18}
                    thickness={2}
                    color={"inherit"}
                    sx={{ position: "absolute", left: "50%", top: "50%", ml: "-9px", mt: "-9px" }}
                />
            </Box>
        ) : (
            <IconPreview size={iconSize} color={"inherit"} />
        );

    const tooltipContent = (
        // 로케일별 문구 길이를 흡수하도록 고정 폭 대신 minWidth~maxWidth 구간을 쓴다.
        <Box sx={{ minWidth: 180, maxWidth: 260, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: theme.palette.common.white }}>
                    {domainLabel}
                </Typography>
                <Box
                    component={"span"}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        flexShrink: 0,
                        px: 0.5,
                        borderRadius: "100px",
                        backgroundColor: statusConfig.color,
                        color: theme.palette.common.white,
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: "nowrap",
                    }}
                >
                    {chipLabel}
                </Box>
            </Box>
            <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
            <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>{infoMessage}</Typography>
            <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200], opacity: 0.8 }}>
                {t(`common::msg::${GLOSS_KEY}`, GLOSS_KEY)}
            </Typography>
        </Box>
    );

    const pill = (
        <Box
            role={"img"}
            aria-label={ariaLabel}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                boxSizing: "border-box",
                borderRadius: "100px",
                border: 1,
                ...statusConfig,
                ...sizeConfig.sx,
                ...(!showLabel && sx),
            }}
        >
            {renderDomainIcon()}
            {!isSmall && renderStatusIcon()}
        </Box>
    );

    return (
        <Tooltip
            title={tooltipContent}
            placement={"bottom"}
            componentsProps={{
                tooltip: {
                    // 디자인: 컨테이너가 콘텐츠를 px 8 / py 4 로 감싼다.
                    sx: { py: 0.5, px: 1, backgroundColor: theme.palette.greySecondary[600] },
                },
            }}
        >
            {showLabel ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, ...sx }}>
                    {pill}
                    <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: statusConfig.color }}>
                        {statusLabel}
                    </Typography>
                </Box>
            ) : (
                pill
            )}
        </Tooltip>
    );
};

AssetProxyStatus.propTypes = {
    status: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    showLabel: PropTypes.bool,
    standalone: PropTypes.bool,
    sx: PropTypes.object,
};

/**
 * 프록시가 아예 없을 때 개요 패널에 놓는 「없음」 + 사유 안내.
 *
 * 이 자산을 단정하지 않고 **제도를 설명한다** — 「프록시 자동 생성은 켠 뒤 등록된 영상에만
 * 적용됩니다」. "활성화 전 등록"과 "추출이 아직 시작 전"은 previews[] 만으로 구분되지 않아
 * 어느 쪽이라고 단정하면 다른 쪽일 때 거짓이 된다. 제도 설명은 두 경우 모두에서 참이고,
 * 사용자가 자기 자산이 어느 쪽인지 스스로 가늠할 수 있다.
 *
 * DAM-482 의 AI 분석 안내도 같은 형태로 맞춘다 — 같은 자산을 두 화면이 다르게 말하지 않도록.
 */
export const AssetProxyAbsent = () => {
    const { t } = useTranslation();
    const label = t(`common::label::${"None"}`, "None");
    const reason = t(`common::msg::${ABSENT_REASON_KEY}`, ABSENT_REASON_KEY);

    return (
        <Tooltip
            title={
                <Box sx={{ minWidth: 180, maxWidth: 260, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: theme.palette.common.white }}>
                        {t("common::label::Proxy video", "Proxy video")}
                    </Typography>
                    <Divider sx={{ borderColor: theme.palette.greySecondary[500] }} />
                    <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200] }}>{reason}</Typography>
                    <Typography sx={{ fontSize: 11, color: theme.palette.greySecondary[200], opacity: 0.8 }}>
                        {t(`common::msg::${GLOSS_KEY}`, GLOSS_KEY)}
                    </Typography>
                </Box>
            }
            placement={"bottom"}
        >
            {/* 아이콘 전용이 아니라 「없음」 글자와 한 묶음이지만, 툴팁이 유일한 사유 전달 경로라
                묶음 전체에 접근 가능한 이름을 준다. */}
            <Box
                component={"span"}
                role={"img"}
                aria-label={`${label} - ${reason}`}
                // 툴팁이 사유를 전달하는 유일한 시각 경로다. tabIndex 가 없으면 포커스가 닿지 않아
                // 마우스로 되는 것을 키보드로 못 한다(WCAG 2.1.1). MUI Tooltip 은 focus 로도 열린다.
                tabIndex={0}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: theme.palette.text.secondary,
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                        borderRadius: "2px",
                    },
                }}
            >
                <Typography variant={"body2_400"}>{label}</Typography>
                <IconInfoCircle size={14} color={"inherit"} />
            </Box>
        </Tooltip>
    );
};

export default AssetProxyStatus;
